import React, { FC, useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';

import CreateAppGroupCampaign from 'components/Campaigns/CreateAppGroupCampaign/CreateAppGroupCampaign';
import EditAppGroupCampaign from 'components/Campaigns/EditAppGroupCampaign/EditAppGroupCampaign';
import { AppGroup } from 'reduxState/store/application/types';
import { ICampaign } from 'reduxState/store/campaigns/types';
import CampaignDashboard from 'components/Campaigns/CampaignsDashboard/CampaignDashboard';
import { useGetCampaignsQuery } from 'reduxState/store/campaigns/api';

/** Hash pattern for viewing a specific campaign: #campaign/<id> */
const CAMPAIGN_HASH_PREFIX = 'campaign';

function getCampaignIdFromHash(): number | null {
  const hash = window.location.hash.slice(1);
  const match = hash.startsWith(CAMPAIGN_HASH_PREFIX + '/')
    ? hash.slice(CAMPAIGN_HASH_PREFIX.length + 1)
    : null;
  if (match == null) return null;
  const id = parseInt(match, 10);
  return Number.isNaN(id) ? null : id;
}

function setCampaignHash(campaignId: number | null): void {
  const path = window.location.pathname + window.location.search;
  const url = campaignId == null ? path : `${path}#${CAMPAIGN_HASH_PREFIX}/${campaignId}`;
  window.history.replaceState(null, '', url);
}

/**
 * AppGroupCampaignManager component for managing campaigns within a specific app group.
 *
 * This component provides a dashboard interface for managing campaigns associated with
 * a particular app group. It handles campaign creation, editing, and duplication while
 * maintaining the proper state management between these operations.
 *
 * @component
 * @param {Object} props - Component props
 * @param {AppGroup} props.appGroup - The app group object containing group details and associated applications
 *
 * @returns {JSX.Element} A dashboard interface for managing app group specific campaigns
 *
 * @example
 * ```tsx
 * <AppGroupCampaignManager
 *   appGroup={myAppGroup}
 * />
 * ```
 */

interface IAppGroupCampaignManager {
  appGroup: AppGroup;
}

/** Wide date range so hash-based open can find any campaign by id */
const HASH_RESOLVE_QUERY_PARAMS = {
  merchantID: null as number | null,
  minStartDate: format(new Date(0), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
  maxEndDate: format(new Date(2099, 11, 31), "yyyy-MM-dd'T'HH:mm:ss'Z'"),
  sortBy: 'modified_date',
  sortOrder: 'asc' as const,
};

const AppGroupCampaignManager: FC<IAppGroupCampaignManager> = ({ appGroup }) => {
  const [selectedCampaignToDuplicate, setSelectedCampaignToDuplicate] = useState<ICampaign | null>(null);
  const [selectedCampaignToEdit, setSelectedCampaignToEdit] = useState<ICampaign | null>(null);
  const [isCreateCampaignModalOpen, setIsCreateCampaignModalOpen] = useState(false);
  const [isEditCampaignModalOpen, setIsEditCampaignModalOpen] = useState(false);

  const { data: campaignsForHash } = useGetCampaignsQuery({
    applicationIDs: appGroup.applications.map(app => app.id),
    ...HASH_RESOLVE_QUERY_PARAMS,
  });

  const handleSetSelectedCampaignToDuplicate = (campaign: ICampaign | null) => {
    setSelectedCampaignToDuplicate(campaign);
    setIsCreateCampaignModalOpen(true);
  };

  const handleSetSelectedCampaignToEdit = useCallback((campaign: ICampaign | null) => {
    setSelectedCampaignToEdit(campaign);
    setIsEditCampaignModalOpen(true);
    if (campaign != null) setCampaignHash(campaign.id);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsCreateCampaignModalOpen(false);
    setIsEditCampaignModalOpen(false);
    setSelectedCampaignToEdit(null);
    setSelectedCampaignToDuplicate(null);
    setCampaignHash(null);
  }, []);

  // Sync hash → state: open edit modal when URL has #campaign/<id> (e.g. shared link in new tab)
  useEffect(() => {
    const syncFromHash = () => {
      const id = getCampaignIdFromHash();
      if (id == null) {
        setSelectedCampaignToEdit(null);
        setIsEditCampaignModalOpen(false);
        return;
      }
      const list = campaignsForHash ?? [];
      const campaign = list.find(c => c.id === id) ?? null;
      if (campaign != null) {
        setSelectedCampaignToEdit(campaign);
        setIsEditCampaignModalOpen(true);
      }
    };
    syncFromHash();
    window.addEventListener('hashchange', syncFromHash);
    return () => window.removeEventListener('hashchange', syncFromHash);
  }, [campaignsForHash]);

  const handleOpenCreateCampaignModal = () => {
    setIsCreateCampaignModalOpen(true);
    setSelectedCampaignToDuplicate(null);
  };

  return (
    <div className="font-montserrat">
      <div className="flex flex-col items-center flex-1 max-w-[1450px] min-w-[500px] mx-auto">
        <h1 className="self-start text-muted-dark-purple">{appGroup.adminName} Campaigns Manager</h1>
        <CampaignDashboard
          appGroup={appGroup}
          handleSetSelectedDuplicatingCampaign={handleSetSelectedCampaignToDuplicate}
          handleSetSelectedEditCampaign={handleSetSelectedCampaignToEdit}
        />
        <div className="flex justify-end w-full">
          <button
            type="button"
            className="w-full sm:w-[200px] h-[40px]  font-bold text-white transition-colors rounded-md text-md bg-muted-dark-purple hover:bg-dark-purple disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="create-campaign-button"
            onClick={handleOpenCreateCampaignModal}
          >
            Add Campaign
          </button>
        </div>

        {selectedCampaignToEdit && (
          <EditAppGroupCampaign
            appGroup={appGroup}
            campaignToEdit={selectedCampaignToEdit}
            setSelectedCampaignToEdit={handleSetSelectedCampaignToEdit}
            handleCloseModal={handleCloseModal}
            isModalOpen={isEditCampaignModalOpen}
          />
        )}
        <CreateAppGroupCampaign
          appGroup={appGroup}
          campaignToDuplicate={selectedCampaignToDuplicate}
          setCampaignToDuplicate={handleSetSelectedCampaignToDuplicate}
          handleCloseModal={handleCloseModal}
          isModalOpen={isCreateCampaignModalOpen}
        />
      </div>
    </div>
  );
};

export default AppGroupCampaignManager;
